import React, { useState } from 'react';
import { ScraperData } from '../../types';
import { ChevronUpIcon, ChevronDownIcon, XIcon } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
interface StatusCardProps {
  data?: ScraperData;
  time: 'AM' | 'PM';
  viewMode: 'day' | 'week' | 'month';
  onCardClick?: (data: ScraperData, time: 'AM' | 'PM') => void;
  isSelected?: boolean;
  isFiltered?: boolean;
}
const StatusCard: React.FC<StatusCardProps> = ({
  data,
  time,
  viewMode,
  onCardClick,
  isSelected = false,
  isFiltered = false
}) => {
  const {
    theme
  } = useTheme();
  const [showTooltip, setShowTooltip] = useState(false);
  // If no data, show grey card for unavailable data
  if (!data) {
    return <div className="w-full h-full bg-[#7f8aa1] border-0" data-id="element-248">
        <div className="w-full h-full flex items-center justify-center" data-id="element-249">
          <span className="text-[8px] text-white" data-id="element-250">-</span>
        </div>
      </div>;
  }
  // If filtered out, show a dark blue/gray cell instead of being transparent
  if (isFiltered) {
    return <div className="w-full h-full bg-[#0F1522] border-0" data-id="element-251">
        <div className="w-full h-full flex items-center justify-center" data-id="element-252">
          <span className="text-[8px] text-gray-700" data-id="element-253">-</span>
        </div>
      </div>;
  }
  // Determine status based on URL change thresholds
  const getStatusFromDelta = () => {
    const delta = Math.abs(data.delta);
    const percentChange = data.previousUrls ? delta / data.previousUrls * 100 : 0;
    // For broken status (>90% drop)
    if (data.delta < 0 && percentChange >= 90) {
      return 'broken';
    }
    // For other statuses based on absolute change
    if (delta >= 50) {
      return 'needsAttention';
    }
    if (delta >= 40 && delta <= 49) {
      return 'major';
    }
    if (delta >= 30 && delta <= 39) {
      return 'moderate';
    }
    if (delta >= 20 && delta <= 29) {
      return 'minor';
    }
    if (delta >= 10 && delta <= 19) {
      return 'routine';
    }
    if (delta >= 1 && delta <= 9) {
      return 'minimal';
    }
    return 'stable';
  };
  // Get the background color for the status card based on a vibrant gradient
  const getCardBackgroundColor = () => {
    const status = getStatusFromDelta();
    switch (status) {
      case 'broken':
        return '#ff00cc';
      // Pink for broken
      case 'needsAttention':
        return '#ef4444';
      // Red for needs attention
      case 'major':
        return '#f97316';
      // Orange for major
      case 'moderate':
        return '#f59e0b';
      // Amber for moderate
      case 'minor':
        return '#eab308';
      // Yellow for minor
      case 'routine':
        return '#84cc16';
      // Lime green for routine
      case 'minimal':
        return '#22c55e';
      // Green for minimal
      case 'stable':
        return '#4ade80';
      // Light green for stable
      default:
        return '#7f8aa1';
      // Grey for unknown status
    }
  };
  // Get the direction arrow for the change
  const getDirectionArrow = () => {
    // Don't show arrows for broken status
    if (getStatusFromDelta() === 'broken') {
      return null;
    }
    // Use consistent small size for all view modes
    const size = 10;
    const strokeWidth = 2.5;
    if (data.delta > 0) {
      return <div className="absolute top-0.5 right-0.5" data-id="element-254">
          <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" data-id="element-255">
            <path d="M12 19V5M5 12l7-7 7 7" data-id="element-256" />
          </svg>
        </div>;
    } else if (data.delta < 0) {
      return <div className="absolute top-0.5 right-0.5" data-id="element-257">
          <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" data-id="element-258">
            <path d="M12 5v14M5 12l7 7 7-7" data-id="element-259" />
          </svg>
        </div>;
    }
    return null;
  };
  // Get status color for text in tooltip
  const getStatusTextColor = () => {
    const status = getStatusFromDelta();
    switch (status) {
      case 'broken':
        return 'text-red-500';
      case 'significant':
        return 'text-orange-500';
      case 'moderate':
        return 'text-yellow-500';
      case 'routine':
      case 'stable':
        return 'text-green-500';
      default:
        return '';
    }
  };
  // Override the status based on our definitions
  const effectiveStatus = getStatusFromDelta();
  const bgColor = getCardBackgroundColor();
  // Handle card click to open modal
  const handleCardClick = () => {
    if (onCardClick) {
      onCardClick(data, time);
    }
  };
  // Determine if we need to show the warning icon for "Broken" status
  const showWarningIcon = effectiveStatus === 'broken';
  // Show tooltip with details, but card only shows status color and arrow
  return <div className={`w-full h-full relative cursor-pointer ${effectiveStatus === 'needsAttention' ? 'border border-red-500' : ''}`} style={{
    backgroundColor: bgColor
  }} onMouseEnter={() => setShowTooltip(true)} onMouseLeave={() => setShowTooltip(false)} onClick={handleCardClick} data-id="element-260">
      {/* Direction arrow - only shown if delta is not 0 and not broken status */}
      {data.delta !== 0 && getDirectionArrow()}

      {/* Warning icon for Broken status */}
      {showWarningIcon && <div className="absolute top-0 right-0 w-full h-full flex items-center justify-center" data-id="element-261">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/91ffpPark4Uc7sq1sq9thV/Icons-exclamation-triangle.png" alt="Warning" className="w-3/5 h-3/5 object-contain opacity-70" data-id="element-262" />
        </div>}

      {/* Tooltip */}
      {showTooltip && !isSelected && <div className={`absolute z-50 w-48 p-2 rounded-md shadow-lg text-xs ${theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`} style={{
      top: '-5px',
      transform: 'translateY(-100%)',
      left: '50%',
      marginLeft: '-96px' // Half of the width to center it
    }} data-id="element-263">
          <div className="font-medium mb-1" data-id="element-264">
            {data.name} - {time}
          </div>
          <div className="grid grid-cols-2 gap-x-2 gap-y-1" data-id="element-265">
            <span className="font-light" data-id="element-266">Status:</span>
            <span className={`font-medium ${getStatusTextColor()}`} data-id="element-267">
              {effectiveStatus === 'needsAttention' ? 'Needs Attention' : effectiveStatus.charAt(0).toUpperCase() + effectiveStatus.slice(1)}
            </span>
            <span className="font-light" data-id="element-268">URLs:</span>
            <span className="font-medium" data-id="element-269">
              {data.urlsScraped.toLocaleString()}
            </span>
            <span className="font-light" data-id="element-270">Change:</span>
            <span className={`font-medium ${data.delta > 0 ? 'text-green-500' : data.delta < 0 ? 'text-red-500' : ''}`} data-id="element-271">
              {data.delta > 0 ? '+' : ''}
              {data.delta.toLocaleString()}
            </span>
            <span className="font-light" data-id="element-272">Date:</span>
            <span className="font-medium" data-id="element-273">{data.date}</span>
          </div>
          <div className="w-full h-2 mt-1 rounded-full" style={{
        backgroundColor: bgColor
      }} data-id="element-274"></div>
        </div>}
    </div>;
};
export default StatusCard;